import logger from '@/utils/logger';

const AUTH_LAST_ACTIVITY_AT_KEY = 'teo:last-auth-activity-at';
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const ACTIVITY_WRITE_INTERVAL_MS = 3_000;

let inMemoryLastActivityAt: number | null = null;
let hasLoggedLocalStorageWriteFailure = false;
let hasLoggedMissingActivityTimestamp = false;

type InactivityTimeoutOptions = {
  /**
   * `clockLost` is set when no activity could be found to count from, which is not
   * the same as time having run out. Another tab's sign-out removes the stamp too.
   */
  onTimeout: (metadata: { idleForMs: number; timeoutMs: number; clockLost?: boolean }) => void;
  timeoutMs?: number;
  /**
   * Counts down from the activity already recorded, and does not count this
   * start as activity. For the same person after their timeout changed: the
   * change is not something they did, so it must not give them a fresh clock.
   */
  resume?: boolean;
};

function canUseBrowserApis(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function canUseLocalStorage(): boolean {
  if (!canUseBrowserApis()) {
    return false;
  }
  try {
    return typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function getLastActivityAt(): number | null {
  let stored: number | null = null;
  if (canUseLocalStorage()) {
    try {
      const value = window.localStorage.getItem(AUTH_LAST_ACTIVITY_AT_KEY);
      const parsed = value ? Number(value) : NaN;
      if (Number.isFinite(parsed)) stored = parsed;
    } catch {
      /* fall through to in-memory */
    }
  }
  if (stored === null) return inMemoryLastActivityAt;
  if (inMemoryLastActivityAt === null) return stored;
  return Math.max(stored, inMemoryLastActivityAt);
}

function setLastActivityAt(timestamp: number): void {
  inMemoryLastActivityAt = timestamp;

  if (canUseLocalStorage()) {
    try {
      window.localStorage.setItem(AUTH_LAST_ACTIVITY_AT_KEY, String(timestamp));
    } catch {
      if (!hasLoggedLocalStorageWriteFailure) {
        hasLoggedLocalStorageWriteFailure = true;
        logger.warn(
          {
            fallbackMode: 'in-memory',
            crossTabSync: false,
          },
          'Auth inactivity timeout could not persist activity to localStorage; using in-memory fallback',
        );
      }
    }
  }
}

function recordAuthActivity(): void {
  setLastActivityAt(Date.now());
}

export function startInactivityTimeout({
  onTimeout,
  timeoutMs = INACTIVITY_TIMEOUT_MS,
  resume = false,
}: InactivityTimeoutOptions): () => void {
  if (!canUseBrowserApis()) {
    return () => {};
  }

  const localStorageAvailable = canUseLocalStorage();
  if (!localStorageAvailable) {
    logger.warn(
      {
        timeoutMs,
        fallbackMode: 'in-memory',
        crossTabSync: false,
      },
      'Auth inactivity timeout is using in-memory fallback because localStorage is unavailable',
    );
  }

  let timeoutId: number | null = null;
  let hasTimedOut = false;

  const clearScheduledTimeout = () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const scheduleTimeout = () => {
    clearScheduledTimeout();

    const now = Date.now();
    const lastActivityAt = getLastActivityAt();

    if (lastActivityAt === null) {
      if (!hasLoggedMissingActivityTimestamp) {
        hasLoggedMissingActivityTimestamp = true;
        logger.warn(
          {
            timeoutMs,
            fallbackMode: 'fail-closed',
          },
          'Auth inactivity timeout could not determine last activity timestamp; expiring session conservatively',
        );
      }

      if (!hasTimedOut) {
        hasTimedOut = true;
        logger.warn(
          {
            idleForMs: timeoutMs,
            timeoutMs,
          },
          'Auth session expired due to inactivity',
        );
        onTimeout({ idleForMs: timeoutMs, timeoutMs, clockLost: true });
      }
      return;
    }

    const remainingMs = timeoutMs - (now - lastActivityAt);

    if (remainingMs <= 0) {
      const idleForMs = now - lastActivityAt;
      if (!hasTimedOut) {
        hasTimedOut = true;
        logger.warn(
          {
            idleForMs,
            timeoutMs,
          },
          'Auth session expired due to inactivity',
        );
        onTimeout({ idleForMs, timeoutMs });
      }
      return;
    }

    timeoutId = window.setTimeout(() => {
      scheduleTimeout();
    }, remainingMs);
  };

  const handleActivity = () => {
    hasTimedOut = false;
    const now = Date.now();
    const lastActivityAt = getLastActivityAt();
    if (lastActivityAt === null || now - lastActivityAt >= ACTIVITY_WRITE_INTERVAL_MS) {
      setLastActivityAt(now);
    }
    scheduleTimeout();
  };

  const handleResumeCheck = () => {
    scheduleTimeout();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleResumeCheck();
    }
  };

  const handleStorage = (event: StorageEvent) => {
    if (localStorageAvailable && event.key === AUTH_LAST_ACTIVITY_AT_KEY) {
      const nextValue = event.newValue === null ? null : Number(event.newValue);
      if (event.newValue === null || !Number.isFinite(nextValue)) {
        inMemoryLastActivityAt = null;
      }
      hasTimedOut = false;
      scheduleTimeout();
    }
  };

  if (!resume) {
    recordAuthActivity();
  }

  scheduleTimeout();

  window.addEventListener('pointerdown', handleActivity);
  window.addEventListener('keydown', handleActivity);
  window.addEventListener('scroll', handleActivity, { passive: true });
  window.addEventListener('focus', handleResumeCheck);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  if (localStorageAvailable) {
    window.addEventListener('storage', handleStorage);
  }

  return () => {
    clearScheduledTimeout();
    window.removeEventListener('pointerdown', handleActivity);
    window.removeEventListener('keydown', handleActivity);
    window.removeEventListener('scroll', handleActivity);
    window.removeEventListener('focus', handleResumeCheck);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (localStorageAvailable) {
      window.removeEventListener('storage', handleStorage);
    }
  };
}

/**
 * For a person whose timeout is `never`: records what they do and counts nothing
 * down, so it never signs anyone out and never takes the fail-closed path.
 *
 * It must still record. Tabs share one stamp, and an older tab that is still on a
 * finite timer reads it. A tab that wrote nothing would let that tab sign out a
 * person who is working here.
 */
export function startActivityRecording({ resume = false }: { resume?: boolean } = {}): () => void {
  if (!canUseBrowserApis()) {
    return () => {};
  }

  const handleActivity = () => {
    const now = Date.now();
    const lastActivityAt = getLastActivityAt();
    if (lastActivityAt === null || now - lastActivityAt >= ACTIVITY_WRITE_INTERVAL_MS) {
      setLastActivityAt(now);
    }
  };

  if (!resume) {
    recordAuthActivity();
  }

  window.addEventListener('pointerdown', handleActivity);
  window.addEventListener('keydown', handleActivity);
  window.addEventListener('scroll', handleActivity, { passive: true });

  return () => {
    window.removeEventListener('pointerdown', handleActivity);
    window.removeEventListener('keydown', handleActivity);
    window.removeEventListener('scroll', handleActivity);
  };
}

/**
 * Forgets when this browser last saw activity, in storage and in memory.
 *
 * For a sign-out: the clock belonged to the person who left, and the next person
 * must not read it. A tab on a finite timer reads a missing stamp as a lost clock
 * and signs out too, which is right because the tokens are gone. A switch between
 * two people does not call this. It writes a new stamp instead, so no tab ever
 * sees the stamp missing.
 */
export function clearAuthActivity(): void {
  inMemoryLastActivityAt = null;

  if (canUseLocalStorage()) {
    try {
      window.localStorage.removeItem(AUTH_LAST_ACTIVITY_AT_KEY);
    } catch {
      /* the in-memory copy is already gone */
    }
  }
}

export const __private = {
  AUTH_LAST_ACTIVITY_AT_KEY,
  INACTIVITY_TIMEOUT_MS,
  recordAuthActivity,
};
